import React, { useMemo, useCallback } from "react";
import { getVal, setVal, TabBarV2 } from "oolib";

import { WidgetHeader } from "../../WidgetBarChart/WIDGET_ELEM_ConfigForm";
import { WidgetFilters } from "../../utils/WidgetFilters";
import { TitleInputWithDefault } from "../../utils/TitleInputWithDefault";
import { getWidgetContentTypes, getWidgetHasDimensions } from "../../utils/getWidgetContentTypes";
import { useGetQueryData } from "@/utils/react-query-hooks/general";

import { SankeyConfigProvider } from "./ConfigForm_Context";
import { ConfigForm_SectionWrapper } from "../../WidgetMetricCard/ConfigForm/ConfigForm_SectionWrapper";
import { ConfigForm_DimensionsSection } from "./ConfigForm_DimensionsSection";
import { ConfigForm_EventFlowSection } from "./ConfigForm_EventFlowSection";
import { EventFlowVisibilityInput } from "./ConfigForm_EventFlowVisibility";
import { DATA_KEYS } from "../DATA_KEYS";
import { useWidgetFilterControls } from "../../utils/useWidgetFilterControls";

const ALL_MODE_OPTIONS = [
  { display: "Dimensional Crossover", value: "dimensionalCrossover" },
  { display: "Sequential Event Flow", value: "sequentialEventFlow" },
];

export const WIDGET_ELEM_ConfigForm = ({
  thisWidgetConfig,
  setConfig,
  underEditWidgetIdx,
  widgetIcon = "FlowArrow",
  widgetTitle = "Sankey Chart"
}) => {

  const { FEATURES_DISABLED_ON_PROD: disabledFeatures = [] } = useGetQueryData("platformConfigs") || {};
  const isProd = import.meta.env.VITE_ENV === "prod";

  const modeOptions = useMemo(() => {
    if (!isProd) return ALL_MODE_OPTIONS;
    return ALL_MODE_OPTIONS.filter((o) => {
      if (o.value === "sequentialEventFlow" && disabledFeatures.includes("sankeyEventFlow")) return false;
      return true;
    });
  }, [isProd, disabledFeatures]);

  const mode = getVal(thisWidgetConfig, DATA_KEYS.mode) || "dimensionalCrossover";

  const handleModeChange = useCallback((_, v) => {
    setConfig((prev) => {
      let widgetConfig = JSON.parse(JSON.stringify(prev[underEditWidgetIdx]));
      widgetConfig = setVal(widgetConfig, DATA_KEYS.mode, v);
      const fullConfig = JSON.parse(JSON.stringify(prev));
      fullConfig[underEditWidgetIdx] = widgetConfig;
      return fullConfig;
    });
  }, [setConfig, underEditWidgetIdx]);

  const widgetType = thisWidgetConfig?.comp || 'WidgetSankeyChart';
  const contentTypesForFilters = useMemo(() => {
    return getWidgetContentTypes(thisWidgetConfig, widgetType);
  }, [thisWidgetConfig, widgetType]);

  const hasDimensions = getWidgetHasDimensions(thisWidgetConfig, widgetType);
  const { filterHeaderProps, filterControlProps } = useWidgetFilterControls({
    forceEnabled: true,
    hasDimensions
  });

  return (
    <SankeyConfigProvider
      thisWidgetConfig={thisWidgetConfig}
      setConfig={setConfig}
      underEditWidgetIdx={underEditWidgetIdx}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.4rem' }}>

        <WidgetHeader title={widgetTitle} type="Chart" icon={widgetIcon} />

        <TabBarV2
          options={modeOptions}
          value={mode}
          onChange={handleModeChange}
          saveValueAsString
          S
        />

        {mode === "dimensionalCrossover" ? (
          <>
            <TitleInputWithDefault
              widgetConfig={thisWidgetConfig}
              widgetType="WidgetSankeyChart"
              setConfig={setConfig}
              underEditWidgetIdx={underEditWidgetIdx}
              titlePath="props.title"
              label="Custom chart title"
              placeholder="Enter title"
            />

            <ConfigForm_DimensionsSection />

            <ConfigForm_SectionWrapper icon="Funnel" title="Filters" {...filterHeaderProps}>
              <WidgetFilters
                widgetConfig={thisWidgetConfig}
                updateWidgetConfig={setConfig}
                widgetIndex={underEditWidgetIdx}
                contentTypesForFilters={contentTypesForFilters}
                {...filterControlProps}
              />
            </ConfigForm_SectionWrapper>
          </>
        ) : (
          <>
            <TitleInputWithDefault
              widgetConfig={thisWidgetConfig}
              widgetType="WidgetSankeyChart"
              setConfig={setConfig}
              underEditWidgetIdx={underEditWidgetIdx}
              titlePath="props.title"
              label="Custom chart title"
              placeholder="Enter title"
            />

            <ConfigForm_EventFlowSection />

            <EventFlowVisibilityInput
              thisWidgetConfig={thisWidgetConfig}
              setConfig={setConfig}
              underEditWidgetIdx={underEditWidgetIdx}
            />
            <ConfigForm_SectionWrapper icon="Funnel" title="Filters" {...filterHeaderProps}>
              <WidgetFilters
                widgetConfig={thisWidgetConfig}
                updateWidgetConfig={setConfig}
                widgetIndex={underEditWidgetIdx}
                contentTypesForFilters={contentTypesForFilters}
                {...filterControlProps}
              />
            </ConfigForm_SectionWrapper>
          </>
        )}

      </div>
    </SankeyConfigProvider>
  );
};
